import React, { useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  Heading,
  Input,
  LoadingIcon,
  Stack,
  Switch,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useAppointmentLocationId,
  useAppointmentPatient,
  useExternalAppointmentId,
  useIsAppointmentReadonly,
  useSignNoteOnEluve,
  useSummary,
  useSyncNoteToEhr,
} from '@eluve/frontend-appointment-hooks';
import { HumanFriendlyError } from '@eluve/frontend-human-friendly-errors';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { useEluveExtExists } from '@eluve/smart-blocks';
import { formatHumanName, toTitleCase } from '@eluve/utils';
import { getVendorDataMapper } from '@eluve/vendor';

import { messageClient } from '../../../../utils/post-messenger';

export const SyncToEhrButton: React.FC = () => {
  const logger = useNamedLogger('SignAndSyncAppointmentButton');
  const toast = useToast();
  const [userProvidedChartUrl, setUserProvidedChartUrl] = useState<
    string | null
  >(null);
  const [manualChartUrlEntry, setManualChartUrlEntry] = useState(false);

  const syncNoteToEhr = useSyncNoteToEhr({
    syncToEhr: messageClient.syncToEhr,
  });
  const signNoteOnEluve = useSignNoteOnEluve();
  const { summary, isEmpty, isSummaryAvailable } = useSummary();

  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState<boolean>(false);
  const [syncNoteDialogOpen, setSyncNoteDialogOpen] = useState<boolean>(false);
  const [syncingInProgress, setSyncingInProgress] = useState<boolean>(false);
  const [syncingEhrError, setSyncingEhrError] =
    useState<HumanFriendlyError | null>(null);
  const [shouldSignExternalChart, setShouldSignExternalChart] = useState(false);

  const tenantId = useTenantIdFromParams() ?? '';

  const appointmentId = useAppointmentId();
  const appointmentExternalId = useExternalAppointmentId();

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
    externalAppointmentId: appointmentExternalId,
  });

  const patient = useAppointmentPatient();
  const patientId = patient?.id;
  const patientFullName = formatHumanName(
    patient?.firstName ?? '',
    patient?.lastName,
  );

  const externalPatientInfo = patient?.external_patients_info?.[0];
  const externalEhr = externalPatientInfo?.external_ehr;
  const ehrName = toTitleCase(externalEhr?.vendor ?? '');
  const ehrVendorProvider = externalEhr?.vendor
    ? getVendorDataMapper(externalEhr.vendor)
    : null;
  const ehrVendorSyncConfig = ehrVendorProvider?.getSyncConfig();

  const patientExternalId = externalPatientInfo?.externalPatientId;

  const patientEhrUrl = ehrVendorProvider?.getPatientEhrUrl({
    domain: externalEhr?.domain ?? '',
    externalPatientId: patientExternalId,
  });
  const { eluveExtExists } = useEluveExtExists();

  const isAppointmentReadonly = useIsAppointmentReadonly();
  const appointmentLocationId = useAppointmentLocationId();

  const showSuccessToast = (text: string, signed: boolean) => {
    toast.success(`Note ${signed ? 'Signed' : 'Drafted'} Successfully`, {
      description: text,
    });
  };

  const onButtonClick = async () => {
    if (ehrVendorSyncConfig?.shouldDisplayConfirmationBeforeSync === false) {
      await syncToExternalEHRAndSignOnEluve();
    } else {
      setConfirmationDialogOpen(true);
    }
  };

  const signNoteOnEluveWithUserProvidedChartUrl = async (): Promise<void> => {
    if (!userProvidedChartUrl) {
      return;
    }
    const chartId =
      ehrVendorProvider?.getChartIdFromChartUrl?.(userProvidedChartUrl);
    if (!chartId) {
      return;
    }

    setConfirmationDialogOpen(false);
    setSyncNoteDialogOpen(false);
    setSyncingEhrError(null);
    const isSubmitted = await signNoteOnEluve({
      chartUrl: userProvidedChartUrl,
      chartId,
    });

    if (isSubmitted) {
      captureEvent('signed_note_on_eluve_only_with_user_provided_chart');
      showSuccessToast(`Your note has been synced.`, shouldSignExternalChart);
    } else {
      captureEvent(
        'failed_to_sign_note_on_eluve_only_with_user_provided_chart',
      );
      toast.error('Failed to sign note.');
    }
  };

  const syncToExternalEHRAndSignOnEluve = async (): Promise<void> => {
    setConfirmationDialogOpen(false);
    setSyncingEhrError(null);
    setSyncNoteDialogOpen(true);
    setSyncingInProgress(true);

    if (!externalEhr) {
      logger.warn(
        `Attempted to sign note for appointment ${appointmentId} without external EHR`,
      );
      return;
    }

    if (!isSummaryAvailable) {
      logger.warn(
        `Attempted to sign note for appointment ${appointmentId} without a summary being available`,
      );
      return;
    }

    const response = await syncNoteToEhr({
      shouldSignExternalChart,
    });

    logger.info('Sync to note EHR response', response);

    if (!response.ok || !response.data) {
      logger.warn('SyncToEhr response was not successful', {
        response,
      });
      if (response?.humanFriendlyError) {
        setSyncingEhrError(response.humanFriendlyError);
        setSyncingInProgress(false);
      }
      return;
    }

    setSyncNoteDialogOpen(false);

    const successSigningOnEluve = await signNoteOnEluve({
      chartUrl: response.data.chartUrl,
      chartId: response.data.chartId,
    });

    if (successSigningOnEluve) {
      captureEvent('signed_note_and_synced_to_external_ehr');
      showSuccessToast(
        `Your note has been successfully synced to ${ehrName}.`,
        shouldSignExternalChart,
      );
    } else {
      captureEvent('failed_to_sign_note_and_sync_to_external_ehr');
      toast.error(
        `Your note has been successfully synced to ${ehrName}, but we experienced an error signing the note on Eluve.`,
      );
    }
  };

  const canSyncNoteToEhr = ehrVendorSyncConfig?.canSyncNoteToEhr({
    externalPatientId: patientExternalId,
    externalAppointmentId: appointmentExternalId,
    eluveExtExists,
    summary: summary!,
  });

  const externalSyncNoteAvailable =
    Boolean(canSyncNoteToEhr?.canSync) &&
    !isEmpty &&
    !isAppointmentReadonly &&
    appointmentLocationId;

  const externalSignNoteAvailable =
    externalSyncNoteAvailable && ehrVendorSyncConfig?.canSignNoteInEhr;

  const externalSyncNoteDisabledReasonText =
    (() => {
      if (isAppointmentReadonly) {
        return 'Only the appointment owner may sync this note.';
      }
      if (isEmpty) {
        return `The chart hasn't been modified. Please fill out the chart before syncing the note.`;
      }
      if (!patientId) {
        return 'Select a patient before syncing the note.';
      }
      if (!appointmentLocationId) {
        return 'Select a location before syncing the note.';
      }
      if (!canSyncNoteToEhr?.canSync) {
        return canSyncNoteToEhr?.reason;
      }
    })() ?? 'Sync to EHR is not available';

  return (
    <div className="w-full">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            disabled={!externalSyncNoteAvailable}
            onClick={onButtonClick}
            fullWidth
          >
            Sync to EHR
          </Button>
        </TooltipTrigger>
        {!externalSyncNoteAvailable && (
          <TooltipContent>{externalSyncNoteDisabledReasonText}</TooltipContent>
        )}
      </Tooltip>

      <Dialog
        open={confirmationDialogOpen}
        onOpenChange={setConfirmationDialogOpen}
      >
        <DialogContent
          size="lg"
          className="flex flex-col items-center justify-center gap-5 p-10"
        >
          <Heading size="3xl">Are you sure?</Heading>
          {externalSignNoteAvailable && (
            <Stack
              direction="horizontal"
              align="center"
              gap={2}
              fullWidth={false}
            >
              <Switch
                checked={shouldSignExternalChart}
                onCheckedChange={setShouldSignExternalChart}
              />
              <Text color="tertiary" size="md" className="text-center">
                Sign external chart
              </Text>
            </Stack>
          )}

          <Text color="secondary" size="md" className="text-center">
            The contents of this note will be exported to {ehrName}{' '}
            {externalSignNoteAvailable && (
              <span>
                {' '}
                as a{' '}
                <span className="font-black text-black">
                  {shouldSignExternalChart ? 'signed note' : 'draft'}
                </span>
              </span>
            )}{' '}
            for patient{' '}
            {patientEhrUrl ? (
              <a
                className="text-text-primary"
                href={patientEhrUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {patientFullName}
              </a>
            ) : (
              patientFullName
            )}
            .
          </Text>

          <Text color="tertiary" size="md" className="text-center">
            You will no longer be able to edit this note in Eluve.
          </Text>

          <Stack direction="horizontal" align="center" justify="center" gap={3}>
            <Button
              className="border border-border-secondary bg-white text-text-tertiary hover:bg-white"
              onClick={() => setConfirmationDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={syncToExternalEHRAndSignOnEluve}>
              {shouldSignExternalChart
                ? 'Yes, Sync and Sign Note'
                : 'Yes, Sync Note as Draft'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={syncNoteDialogOpen} onOpenChange={setSyncNoteDialogOpen}>
        <DialogContent
          size="lg"
          className="flex flex-col items-center justify-center gap-5 p-10"
        >
          {syncingInProgress && (
            <div>
              <Stack align="center">
                <Heading size="3xl">Syncing to {ehrName}</Heading>
              </Stack>
              <Text color="tertiary" size="md" className="m-3 text-center">
                We are in the process of syncing to {ehrName}. This should only
                take a few moments.
              </Text>
              <Stack direction="horizontal" align="center" justify="center">
                <LoadingIcon
                  size="xl"
                  className="m-8 size-12! text-bg-teal-strong"
                />
              </Stack>
            </div>
          )}

          {syncingEhrError && (
            <div>
              {manualChartUrlEntry ? (
                <div>
                  <Stack className="m-3" align="center">
                    <Heading size="3xl">Add Chart URL</Heading>

                    <Input
                      className="m-5"
                      type="text"
                      placeholder="Chart URL"
                      onChange={(e) => setUserProvidedChartUrl(e.target.value)}
                    />
                    <Stack
                      direction="horizontal"
                      align="center"
                      justify="center"
                      gap={3}
                    >
                      <Button
                        className="border border-border-secondary bg-white text-text-tertiary hover:bg-white"
                        onClick={() => setManualChartUrlEntry(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={
                          !userProvidedChartUrl ||
                          !userProvidedChartUrl.startsWith('http') ||
                          !ehrVendorProvider?.getChartIdFromChartUrl?.(
                            userProvidedChartUrl,
                          )
                        }
                        onClick={async () => {
                          await signNoteOnEluveWithUserProvidedChartUrl();
                          setSyncingEhrError(null);
                          setSyncingInProgress(false);
                        }}
                      >
                        Add Chart URL and Sign
                      </Button>
                    </Stack>
                  </Stack>
                </div>
              ) : (
                <div>
                  <Stack className="m-3" align="center">
                    <Heading size="3xl">{syncingEhrError.title}</Heading>
                  </Stack>
                  <div className="mb-6">
                    <Text color="tertiary" size="md" className="text-center">
                      {syncingEhrError.message}
                    </Text>
                  </div>
                  <Stack
                    direction="horizontal"
                    align="center"
                    justify="center"
                    gap={3}
                  >
                    <Button
                      className="border border-border-secondary bg-white text-text-tertiary hover:bg-white"
                      onClick={() => setSyncNoteDialogOpen(false)}
                    >
                      Cancel
                    </Button>

                    {ehrVendorSyncConfig?.canSyncWithManualChartUrl &&
                      ehrVendorProvider?.getChartIdFromChartUrl && (
                        <Button
                          className="border border-border-secondary bg-white text-text-tertiary hover:bg-white"
                          onClick={() => {
                            setManualChartUrlEntry(true);
                          }}
                        >
                          Manually Add Chart URL
                        </Button>
                      )}
                    <Button onClick={syncToExternalEHRAndSignOnEluve}>
                      Try Again
                    </Button>
                  </Stack>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
